/**
 * buscaTolerante — casamento de texto tolerante a acento, caixa, pontuação,
 * ordem das palavras e erro de digitação (pt-BR).
 *
 * Nasceu para o campo "Material (Cores MDF)" das solicitações ASM/ASP/AST:
 * o montador digita no celular, no canteiro, e "carvalio", "brnco",
 * "grafitte" ou "cinza cristal" precisam achar "Carvalho Hanover",
 * "Branco TX", "Grafite" e "Cristal Cinza".
 *
 * Estratégia, por palavra digitada (cada palavra precisa casar em ALGUMA
 * palavra do alvo — ordem não importa):
 *   1. substring da forma normalizada (acento/caixa removidos);
 *   2. substring da forma fonética (ç/ss/s/z, ch/x, ph/f, lh/l, nh/n…);
 *   3. distância de edição (Levenshtein) contra o prefixo/palavra do alvo,
 *      com tolerância proporcional ao tamanho da palavra digitada.
 *
 * `pontuar` devolve um score p/ ordenar: casamento exato/prefixo vale mais
 * que fonético, que vale mais que correção de erro.
 */

const SEM_MATCH = 0;

export function normalizar(texto) {
  return String(texto || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * Reduz grafias que soam igual em pt-BR a uma forma única. Não é um
 * algoritmo fonético completo — só cobre as trocas que aparecem de verdade
 * quando alguém escreve "de ouvido".
 */
export function chaveFonetica(palavra) {
  // "ç" vira "c" na normalização (perde a cedilha) — mas soa "s".
  const semCedilha = String(palavra || "").replace(/ç/gi, "s");
  return normalizar(semCedilha)
    .replace(/ph/g, "f")
    .replace(/ch/g, "x")
    .replace(/sh/g, "x")
    .replace(/lh/g, "l")
    .replace(/nh/g, "n")
    .replace(/qu/g, "k")
    .replace(/ck/g, "k")
    .replace(/c([ei])/g, "s$1")
    .replace(/c/g, "k")
    .replace(/ss/g, "s")
    .replace(/sc/g, "s")
    .replace(/z/g, "s")
    .replace(/x/g, "s")
    .replace(/g([ei])/g, "j$1")
    .replace(/w/g, "v")
    .replace(/y/g, "i")
    .replace(/h/g, "")
    .replace(/(.)\1+/g, "$1");
}

export function distanciaEdicao(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let anterior = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    const atual = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const custo = a[i - 1] === b[j - 1] ? 0 : 1;
      atual[j] = Math.min(
        anterior[j] + 1,
        atual[j - 1] + 1,
        anterior[j - 1] + custo,
      );
      // Transposição de letras vizinhas ("grfaite") conta como 1 erro.
      if (
        i > 1 && j > 1
        && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]
      ) {
        atual[j] = Math.min(atual[j], anterior[j - 1]);
      }
    }
    anterior = atual;
  }
  return anterior[b.length];
}

/** Quantos erros de digitação uma palavra desse tamanho pode ter. */
function tolerancia(tamanho) {
  if (tamanho <= 3) return 0;
  if (tamanho <= 6) return 1;
  return 2;
}

const PESO_EXATO = 3;
const PESO_FONETICO = 2;
const PESO_CORRIGIDO = 1;

function pontuarPalavra(termo, palavrasAlvo) {
  if (palavrasAlvo.some((p) => p.normal.includes(termo.normal))) return PESO_EXATO;
  if (palavrasAlvo.some((p) => p.fonetica.includes(termo.fonetica))) return PESO_FONETICO;
  const maxErros = tolerancia(termo.normal.length);
  if (maxErros === 0) return SEM_MATCH;
  const cabe = palavrasAlvo.some((p) => {
    const prefixo = p.normal.slice(0, termo.normal.length);
    return (
      distanciaEdicao(termo.normal, p.normal) <= maxErros
      || distanciaEdicao(termo.normal, prefixo) <= maxErros
      || distanciaEdicao(termo.fonetica, p.fonetica) <= maxErros
    );
  });
  return cabe ? PESO_CORRIGIDO : SEM_MATCH;
}

function tokenizar(texto) {
  return normalizar(texto)
    .split(" ")
    .filter(Boolean)
    .map((normal) => ({ normal, fonetica: chaveFonetica(normal) }));
}

/**
 * Score de `consulta` contra `alvo` (string ou lista de strings).
 * 0 = não casa. Maior = casamento mais fiel ao que foi digitado.
 */
export function pontuar(consulta, alvo) {
  const termos = tokenizar(consulta);
  if (!termos.length) return SEM_MATCH;
  const textoAlvo = Array.isArray(alvo) ? alvo.join(" ") : alvo;
  const palavrasAlvo = tokenizar(textoAlvo);
  if (!palavrasAlvo.length) return SEM_MATCH;

  // Consulta inteira como substring (ex.: "cinza cristal" em "cristal
  // cinza" NÃO passa aqui, mas "branco tx" em "branco tx 18" passa) — vale
  // um bônus para ficar acima dos casamentos por palavra solta.
  const consultaNormal = normalizar(consulta);
  const bonusFrase = normalizar(textoAlvo).includes(consultaNormal) ? 1 : 0;

  let total = 0;
  for (const termo of termos) {
    const pontos = pontuarPalavra(termo, palavrasAlvo);
    if (pontos === SEM_MATCH) return SEM_MATCH;
    total += pontos;
  }
  return total + bonusFrase;
}

/**
 * Filtra e ordena `itens` pela consulta. `extrair(item)` devolve a string
 * (ou lista de strings) em que se busca. Empate preserva a ordem original.
 */
export function filtrarTolerante(itens, consulta, extrair) {
  if (!normalizar(consulta)) return itens;
  return itens
    .map((item, ordem) => ({ item, ordem, score: pontuar(consulta, extrair(item)) }))
    .filter((r) => r.score > SEM_MATCH)
    .sort((a, b) => b.score - a.score || a.ordem - b.ordem)
    .map((r) => r.item);
}
